import { kebabCase } from 'lodash'
import sha256 from 'crypto-js/sha256'
import { loadFirstToken, loadToken } from 'context/UserSession/userSessionReducer'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
import strings from '../l10n'
import regex from './regex'
import { currencyFormat } from './format'

export function getTokenContent(token: string | null) {
  const base64Url = token?.split('.')[1]
  const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/')
  const tokenContent = JSON.parse(atob(base64 ?? ''))
  return tokenContent
}

export function getDocumentDownloadUrl(fileUrl: string) {
  const jwtToken = loadToken()
  const factorId = getTokenContent(jwtToken).factorId

  const url = new URL(fileUrl)
  url.pathname = `/clientportal${url.pathname}`

  const buildedUrl = `${url.toString()}?factorId=${factorId}&authToken=${jwtToken}`
  return buildedUrl
}

export function triggerFactorcloudDocumentDownload(fileUrl: string) {
  const url = getDocumentDownloadUrl(fileUrl)
  window.open(url, '_blank')
}

export function minValueLimit(value: number, text?: string) {
  const message =
    text ||
    `${strings.formatString(strings.NUMBER_MUST_BE_GREATER_EQUALS_THAN, {
      number: value,
    })}`
  return {
    value: value,
    message: message,
  }
}

export function maxLengthLimit(value: number, text?: string) {
  const message =
    text ||
    `${strings.formatString(strings.TEXT_MUST_BE_LESS_THAN, {
      number: value,
    })}`

  return {
    value: value,
    message: message,
  }
}

export function getCurrentToken() {
  const firstToken = loadFirstToken()
  const loginToken = loadToken()
  const currentToken = firstToken ? firstToken : loginToken
  return currentToken
}

export function slugify(arg: string) {
  return kebabCase(arg)
}

export function hash(arg: any) {
  const str = JSON.stringify(arg)
  const hash = sha256(str).toString()
  return hash
}

/**
 * Returns true is val is not null nor undefined
 * @param arg to check
 * @returns boolean result
 */
export function isDefined<T>(arg: T | null | undefined): arg is T {
  return typeof arg != 'undefined' && arg !== null
}

/**
 * Returns true if arg is null, undefined or ""
 * @param arg to check
 * @returns boolean result
 */
export function empty(arg: string | null | undefined) {
  return isDefined(arg) !== true || arg === ''
}

/**
 * Returns val if condition is True. Return undefined otherwise
 * @param arg to be returned
 * @param condition to check
 * @returns val or undefined
 */
export function ift<T>(arg: T, condition: boolean) {
  if (condition) return arg
}

export function formatDate(invoiceDate: string) {
  const currentDate = new Date().toISOString().split('T')[1]
  return invoiceDate.concat('T').concat(currentDate)
}

export function isUpdatedPaymentDate(invoiceDate: string, dataInvoiceDate: string) {
  return invoiceDate.split('T')[0] === dataInvoiceDate ? invoiceDate : formatDate(dataInvoiceDate)
}

export function getIndexOfColumn(column: string, columns: GridColDef[]) {
  return columns.findIndex((col) => col.field === column)
}

export function handleZipLabel(countryCode?: string) {
  if (countryCode === 'US' || isDefined(countryCode) === false) {
    return strings.ZIP
  }
  return strings.POSTAL_CODE
}

export function handleZipPostalCodeLength(countryCode?: string) {
  if (countryCode === 'CA' || isDefined(countryCode) === false) {
    return 7
  }
  return 10
}

export function handleZipLabelMaxLengthMessage(countryCode?: string) {
  if (countryCode === 'CA' || isDefined(countryCode) === false) {
    return strings.MAX_LENGTH_POSTAL_CODE
  }
  return strings.MAX_LENGTH_ZIP_CODE
}
export function handleZipCodeRegex(countryCode?: string) {
  if (countryCode === 'CA' || isDefined(countryCode) === false) {
    return regex.POSTAL_CODE_REGEX_PATTERN
  }
  return regex.ZIP_CODE_REGEX_PATTERN
}
export function handleRegexErrorMessage(countryCode?: string) {
  if (countryCode === 'CA' || isDefined(countryCode) === false) {
    return strings.INVALID_POSTAL_CODE
  }
  return strings.INVALID_ZIP_CODE
}

export function buildUri(...components: string[]) {
  return components.map((c) => encodeURIComponent(c)).join('/')
}

export function isBlank(value: string | null) {
  return value === null || value.trim() === ''
}

export function isNotBlank(value?: string | null) {
  return isDefined(value) && !isBlank(value)
}

export function genericHandleSortModelChange(model: GridSortModel, setSortValue?: (value: string) => void) {
  if (!isDefined(setSortValue)) return
  if (model.length > 0) {
    setSortValue(`${model[0].field}:${model[0].sort?.toUpperCase()}`)
  } else {
    setSortValue('')
  }
}

export function mapDateFilters(
  date: any,
  setStartDate: (value: Date | undefined) => void,
  setEndDate: (value: Date | undefined) => void
) {
  setStartDate(undefined)
  setEndDate(undefined)
  if (isDefined(date)) {
    if (isDefined(date[0])) {
      setStartDate(date[0])
    }
    if (isDefined(date[1])) {
      setEndDate(date[1])
    }
  }
}

/**
 * Rounds up a number to the next "nice" number.
 *
 * This function uses a predefined set of commonly used scaling steps
 * (like 1, 1.1, 1.25, 1.5, etc.) to round a given number up to a
 * more human-readable and graph-friendly value.
 *
 * For example:
 * - 1623 -> 2000
 * - 1340 -> 1500
 * - 1234 → 1250
 * - 123  → 125
 * - 44   → 50
 *
 * @param num - The number to round up.
 * @returns The next "nice" number greater than or equal to the input.
 */
export function roundUpToNiceNumber(num: number): number {
  if (num <= 10) return 10

  const exponent = Math.floor(Math.log10(num))
  const base = Math.pow(10, exponent)

  // Nice steps are the usual steps used in charts and graphs
  const niceSteps = [1, 1.1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 9, 10]

  for (const step of niceSteps) {
    const candidate = step * base
    if (candidate >= num) {
      return candidate
    }
  }

  // Fallback in case all steps are below the number
  return 10 * base
}

export function maxValueLimit(value?: number | string | null, text?: string) {
  if (!isDefined(value) || value === '') return undefined
  const message =
    text ||
    `${strings.formatString(strings.NUMBER_MUST_BE_LESS_EQUALS_THAN, {
      number: value,
    })}`

  return {
    value: value,
    message: message,
  }
}

/**
 * Formats a number into a human-readable abbreviated currency string.
 *
 * Depending on the magnitude of the number, it appends a suffix:
 * - 'K' for thousands (>= 1,000 and < 1,000,000)
 * - 'M' for millions (>= 1,000,000 and < 1,000,000,000)
 * - 'B' for billions (>= 1,000,000,000)
 * If the number is less than 1,000, no abbreviation is added.
 *
 * Example outputs:
 * - 950 → "$950"
 * - 1500 → "$1.5K"
 * - 2500000 → "$2.5M"
 * - 7800000000 → "$7.8B"
 *
 * @param number - The number to be formatted. Defaults to 0 if not provided.
 * @returns A formatted currency string with the appropriate abbreviation.
 */
export function getNumberWithQuantityAbbreviation(number: number = 0): string {
  let value: number
  let abbreviation: string

  const exponent: number = Math.floor(Math.log10(number))

  if (exponent >= 3 && exponent < 6) {
    value = number / 1000
    abbreviation = 'K'
  } else if (exponent >= 6 && exponent < 9) {
    value = number / 1000000
    abbreviation = 'M'
  } else if (exponent >= 9) {
    value = number / 1000000000
    abbreviation = 'B'
  } else {
    value = number
    abbreviation = ''
  }

  return `${currencyFormat(value, 'USD', 0)}${abbreviation}`
}
